import {createContext, useContext, useMemo} from "react";
import type {ReactTable, RowData} from "@tanstack/react-table";
import type {DataTableFeatures} from "./useDataTable";

interface DataTableContextValue<TData extends RowData> {
	table: ReactTable<DataTableFeatures, TData>;
	loading: boolean;
}

const DataTableContext = createContext<DataTableContextValue<RowData> | null>(null);

/* Stored type-erased (`RowData`); each part re-applies its own `TData`. */
function useDataTableContext<TData extends RowData>(part: string): DataTableContextValue<TData> {
	const context = useContext(DataTableContext);
	if (context === null) {
		throw new Error(`<DataTable.${part}> must be rendered inside <DataTable.Root>.`);
	}
	return context as unknown as DataTableContextValue<TData>;
}

/** Provides the table instance to the `DataTable.*` parts; the markup comes from `DataTable.Content`. */
function DataTableRoot<TData extends RowData>({
	table,
	loading = false,
	className,
	...props
}: React.ComponentProps<"div"> & {
	/** The TanStack table instance, usually from `useDataTable`. */
	table: ReactTable<DataTableFeatures, TData>;
	/** Shows the loading overlay over the content while server data is in flight. */
	loading?: boolean;
}) {
	const value = useMemo(() => ({table: table as ReactTable<DataTableFeatures, RowData>, loading}), [table, loading]);
	return (
		<DataTableContext.Provider value={value}>
			<div data-slot="data-table" className={className} {...props} />
		</DataTableContext.Provider>
	);
}

export {DataTableRoot, useDataTableContext};
